import { Eye, HeartStraight, Pause, VideoCamera } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogAbsenteeBidders, getCatalogHasVideo } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogBidders } from '@/redux/modules/console';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';

type Props = {
    catalogId?: number;
    className?: string;
    isPaused?: boolean;
    itemId?: number;
};

// no direct hammer-ui tokens for shadowOverlay50 (rgba(255,255,255,0.8)) or grey100 (#656565)
const pillBaseSyles = 'inline-flex items-center rounded-3xs bg-[rgba(255,255,255,0.8)] px-xs text-left text-[#656565]';

const ConsoleSocialComponent = ({ catalogId, className = '', isPaused, itemId }: Props) => {
    const isMobile = useIsMobileLayout();
    const catalogIdFromItem = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const saved = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId || catalogIdFromItem));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId || catalogIdFromItem));

    let savedCount = 0;
    if (!catalogIdFromItem && itemId) {
        savedCount = saved;
    }

    return (
        <div
            className={cn(
                'absolute left-0 z-[2] flex select-none flex-col items-start',
                isMobile ? 'ml-sm mt-xs' : 'ml-2xs mt-2xs',
                className
            )}
        >
            <>
                {isPaused && (
                    <div
                        className={cn(pillBaseSyles, 'mb-2xs', {
                            'rounded-br-none rounded-tr-none pr-0': isPaused,
                        })}
                    >
                        <BodySecondary className="center flex">
                            {hasVideo && (
                                <VideoCamera
                                    className="mr-2xs"
                                    color="primary"
                                    size="sm"
                                    style="fill"
                                />
                            )}
                            {isPaused && (
                                <Pause
                                    className="mr-2xs"
                                    color="primary"
                                    size="sm"
                                    style="bold"
                                />
                            )}
                            <FormattedMessage id={isPaused ? 'paused' : 'live'} />
                        </BodySecondary>
                    </div>
                )}
                <div
                    className={cn(pillBaseSyles, {
                        'rounded-bl-none rounded-tl-none': !isPaused,
                    })}
                >
                    <Eye
                        className="mr-2xs"
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>
                        <FormattedNumber
                            key="console-social-bidders"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            value={bidders + absenteeBidders || 1}
                        />
                    </BodySecondary>
                </div>
            </>
            {Boolean(savedCount) && (
                <div className={cn(pillBaseSyles, 'rounded-bl-none rounded-tl-none')}>
                    <HeartStraight
                        className="mr-2xs"
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>
                        <FormattedNumber
                            key="console-social-saved-count"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            value={savedCount}
                        />
                    </BodySecondary>
                </div>
            )}
        </div>
    );
};

export const ConsoleSocial = memo<Props>(ConsoleSocialComponent);
